import React, { useEffect, useState } from 'react';
import * as fornecedoresApi from '../../api/fornecedores';
import { useEmpresaSelecionada } from '../../contexts/EmpresaContext';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import SearchableSelect from '../../components/common/SearchableSelect';
import StatusBadge from '../../components/common/StatusBadge';
import { useSortable } from '../../hooks/useSortable';
import { getErrorMessage } from '../../utils/apiError';

const VAZIO = { nome: '', documento: '', telefone: '' };

export default function Fornecedores() {
  const { modoGlobal, empresas, idEmpresa } = useEmpresaSelecionada();

  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState(VAZIO);
  const [idEmpresaForm, setIdEmpresaForm] = useState('');
  const [editando, setEditando] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const carregar = () => {
    setLoading(true);
    fornecedoresApi
      .listFornecedores(mostrarInativos)
      .then((r) => setItens(r.data))
      .catch(() => setErro('Erro ao carregar fornecedores.'))
      .finally(() => setLoading(false));
  };

  // idEmpresa muda quando o super_admin troca de empresa no seletor do topbar — sem isso, a
  // lista continuaria mostrando os cadastros da empresa anterior até uma navegação manual.
  useEffect(carregar, [mostrarInativos, idEmpresa]);

  const filtrados = itens.filter((i) => i.nome.toLowerCase().includes(busca.toLowerCase()));
  const { sorted, Th } = useSortable(filtrados, 'nome');

  const abrirNovo = () => {
    setForm(VAZIO);
    setIdEmpresaForm('');
    setEditando(null);
    setMostrarForm(true);
  };

  const abrirEdicao = (item) => {
    setForm({ nome: item.nome, documento: item.documento || '', telefone: item.telefone || '' });
    setEditando(item);
    setMostrarForm(true);
  };

  const alternarAtivo = async (item) => {
    const acao = item.ativo ? 'Desativar' : 'Reativar';
    const aviso = item.ativo ? ' Ele deixa de aparecer nesta lista e nos seletores de busca.' : '';
    if (!window.confirm(`${acao} "${item.nome}"?${aviso}`)) return;
    setErro('');
    try {
      await fornecedoresApi.updateFornecedor(item.id_fornecedor, { ativo: !item.ativo }, item.id_empresa);
      carregar();
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao alterar o status do fornecedor.'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!editando && modoGlobal && !idEmpresaForm) {
      setErro('Selecione a empresa dona deste cadastro.');
      return;
    }
    setSalvando(true);
    try {
      if (editando) await fornecedoresApi.updateFornecedor(editando.id_fornecedor, form, editando.id_empresa);
      else await fornecedoresApi.createFornecedor(form, idEmpresaForm);
      setMostrarForm(false);
      carregar();
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <PageHeader titulo="Fornecedores">
        {!mostrarForm && (
          <button type="button" className="sf-btn sf-btn-primary" onClick={abrirNovo}>
            <i className="bi bi-plus-lg" />
            Novo fornecedor
          </button>
        )}
      </PageHeader>

      {erro && (
        <div className="sf-login-alert mb-3">
          <i className="bi bi-exclamation-circle" />
          {erro}
        </div>
      )}

      {mostrarForm && (
        <form onSubmit={handleSubmit} className="sf-card p-4 mb-3" style={{ maxWidth: 560 }}>
          {editando && modoGlobal && (
            <div className="sf-badge sf-badge-neutral sf-badge-plain mb-3">
              <i className="bi bi-building" /> {editando.empresa.nome}
            </div>
          )}
          {!editando && modoGlobal && (
            <div className="sf-field-label mb-3">
              <span>Empresa</span>
              <SearchableSelect
                options={empresas.map((e) => ({ value: e.id_empresa, label: e.nome }))}
                value={idEmpresaForm}
                onChange={setIdEmpresaForm}
                placeholder="Selecione a empresa..."
                required
              />
            </div>
          )}
          <label className="sf-field-label mb-3">
            <span>Nome</span>
            <span className="sf-field">
              <input required autoFocus value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            </span>
          </label>
          <div className="row g-3">
            <div className="col-6">
              <label className="sf-field-label">
                <span>Documento (CNPJ/CPF)</span>
                <span className="sf-field">
                  <input value={form.documento || ''} onChange={(e) => setForm({ ...form, documento: e.target.value })} />
                </span>
              </label>
            </div>
            <div className="col-6">
              <label className="sf-field-label">
                <span>Telefone</span>
                <span className="sf-field">
                  <input value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
                </span>
              </label>
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button type="submit" className="sf-btn sf-btn-primary" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
            <button type="button" className="sf-btn sf-btn-ghost" onClick={() => setMostrarForm(false)}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="sf-table-card">
        <div className="sf-table-toolbar">
          <div className="sf-search">
            <i className="bi bi-search" />
            <input placeholder="Buscar fornecedor..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
            Mostrar inativos
          </label>
        </div>
        {loading ? (
          <div className="p-4">
            <Skeleton linhas={5} />
          </div>
        ) : sorted.length === 0 ? (
          <EmptyState icon="bi-truck-flatbed" titulo="Nenhum fornecedor encontrado" texto="Ajuste a busca ou cadastre um novo fornecedor." />
        ) : (
          <div className="table-responsive">
            <table className="sf-table">
              <thead>
                <tr>
                  <Th campo="nome">Nome</Th>
                  <th>Documento</th>
                  <th>Telefone</th>
                  {modoGlobal && <th>Empresa</th>}
                  {mostrarInativos && <th>Status</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((f) => (
                  <tr key={f.id_fornecedor}>
                    <td>
                      <span className="d-flex align-items-center gap-2">
                        <span className="sf-cell-icon"><i className="bi bi-truck-flatbed" /></span>
                        <span className="sf-cell-title">{f.nome}</span>
                      </span>
                    </td>
                    <td>{f.documento || '—'}</td>
                    <td>{f.telefone || '—'}</td>
                    {modoGlobal && <td className="sf-cell-sub">{f.empresa.nome}</td>}
                    {mostrarInativos && <td><StatusBadge status={f.ativo ? 'ativo' : 'inativo'} /></td>}
                    <td className="text-end">
                      <div className="d-flex gap-2 justify-content-end">
                        <button type="button" className="sf-btn sf-btn-ghost" onClick={() => abrirEdicao(f)} title="Editar">
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="sf-btn sf-btn-ghost"
                          style={f.ativo ? { color: 'var(--sf-danger-fg)', borderColor: '#f0cfcc' } : undefined}
                          onClick={() => alternarAtivo(f)}
                          title={f.ativo ? 'Desativar' : 'Reativar'}
                        >
                          <i className={`bi ${f.ativo ? 'bi-slash-circle' : 'bi-check-circle'}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
